import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import {
  MonetizationIcon,
  Button,
  EditIcon,
  DrawerStepper,
} from '@mercai/clever'
import { CardInfo, CreditOrderForm } from '@/components'

import { useAuth } from '@/providers'
import {
  QUERIES_ADMIN,
  useCreateCreditOrder,
  useUpdateCreditOrder,
} from '@/services'
import {
  cleanObject,
  formatCurrency,
  formatDate,
  isEnabledCreditOrderTracker,
  onErrorMessage,
  onSuccessMessage,
} from '@/helpers'

import { CreditOrderProps } from '@/types'
import type { CardOrderCreditProps } from './card-order-credit.type'

import styles from './card-order-credit.module.css'

export const CardOrderCredit = ({
  isLoading,
  paymentMethod,
  orderDate,
  totalOrderPrice,
  saleOrder,
}: CardOrderCreditProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('componentCardsOrderCenterShow')
  const queryClient = useQueryClient()
  const { whoAmI } = useAuth()

  const [profileCreditSelected, setProfileCreditSelected] = useState(false)
  const [values, setValues] = useState<Partial<CreditOrderProps>>({})

  const creditOrder = saleOrder?.creditOrders?.[0]
  const isNewRecord = !creditOrder?.uuid

  const { mutateAsync: mutateAsyncCreate } = useCreateCreditOrder()
  const { mutateAsync: mutateAsyncUpdate } = useUpdateCreditOrder(
    creditOrder?.uuid,
  )

  const onHandleSubmit = async () => {
    const mutateAsync = isNewRecord ? mutateAsyncCreate : mutateAsyncUpdate

    const payload: Partial<CreditOrderProps> = {
      debtorUuid: saleOrder?.buyer?.uuid || '',
      amount: saleOrder?.totalOrderPrice || '0',
      orderUuid: saleOrder?.uuid || '',
      paymentTerm: values?.paymentTerm,
      status: values?.status,
      interest: values?.interest ? Number(values.interest).toFixed(4) : '0',
      expectedPaymentDate: values?.expectedPaymentDate,
      paidAt: values?.paidAt,
    }

    const result = await mutateAsync(cleanObject(payload))

    if (result.success) {
      onSuccessMessage(
        t('orderCreditformSubmitSuccessTitle'),
        t('orderCreditformSubmitSuccessMessage'),
      )

      setProfileCreditSelected(false)
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, saleOrder?.uuid],
      })
    } else {
      onErrorMessage(
        t('orderCreditformSubmitErrorTitle'),
        t('orderCreditformSubmitErrorMessage'),
      )
    }
  }

  useEffect(() => {
    if (creditOrder) {
      setValues({
        amount: creditOrder?.amount || '0',
        interest: creditOrder?.interest || '0.02',
        paymentTerm: creditOrder?.paymentTerm || '30',
        status: creditOrder?.status || 'pending',
        expectedPaymentDate: creditOrder?.expectedPaymentDate || '',
        paidAt: creditOrder?.paidAt || '',
      })
    }
  }, [creditOrder, profileCreditSelected])

  return (
    <>
      <div className={styles.core}>
        <CardInfo
          isLoading={isLoading}
          icon={<MonetizationIcon />}
          title={t('orderCreditTitle')}
          actions={
            isEnabledCreditOrderTracker(whoAmI?.email) ? (
              <Button
                variant="ghost"
                className={styles.edit}
                size="small"
                onClick={() => setProfileCreditSelected(true)}
              >
                {t('editLabel')}
                <EditIcon />
              </Button>
            ) : null
          }
          items={[
            {
              label: t('orderCreditTotalValueLabel'),
              value: totalOrderPrice
                ? formatCurrency(totalOrderPrice, 'es-MX', 'USD')
                : '-',
            },
            {
              label: t('orderCreditCreditCostLabel'),
              value: creditOrder
                ? formatCurrency(creditOrder?.amount, 'es-MX', 'USD')
                : '-',
            },
            {
              label: t('orderCreditPaymentMethodLabel'),
              value: paymentMethod
                ? tEnums(`financial_profile_payment_forms_${paymentMethod}`)
                : '-',
            },
            {
              label: t('orderCreditDeadlineLabel'),
              value: `${creditOrder?.paymentTerm} ${t('paymentRatesOptionsLabel')}`,
            },
            {
              label: t('orderCreditOrderDateLabel'),
              value: formatDate(orderDate || ''),
            },
            {
              label: t('orderCreditExpectedPaymentDateLabel'),
              value: formatDate(creditOrder?.expectedPaymentDate || ''),
            },
            {
              label: t('orderCreditPaymentDateLabel'),
              value: creditOrder?.paidAt
                ? formatDate(creditOrder?.paidAt)
                : '-',
            },
          ]}
        />
      </div>

      <DrawerStepper
        initialStep="root"
        isOpen={profileCreditSelected}
        onHandleOpen={() => setProfileCreditSelected(false)}
        title={t('orderCreditTitle')}
        translations={{
          optional: '',
        }}
        steps={{
          root: {
            backNavigate: 'root',
            component: (
              <CreditOrderForm
                onChangeValues={setValues}
                values={values}
                onHandleSubmit={onHandleSubmit}
                onBack={() => setProfileCreditSelected(false)}
              />
            ),
            isOptional: false,
          },
        }}
      />
    </>
  )
}
